import { useState } from "react";
import { money } from "../lib/store";
import { feeDiscount } from "../lib/billing";
import { Icon } from "../components/Icons";
import type { Project, Deliverable } from "../lib/types";

/* Fees & scope for a project billed from a SIGNED CONTRACT.
   QuotationDoc prices the job off the rate card, which is right when the studio
   is still quoting and badly wrong once terms are agreed — it invents a fee
   nobody signed and a budget the project never carried. This reads the agreed
   structure instead, and hangs the drawings and renders off the discipline and
   stage that actually pays for them. */

/* The contract names disciplines in full ("Structural engineering &
   consultancy"); drawings are filed under the short label. Match on intent. */
const DISCIPLINE_MATCH: { test: RegExp; covers: string[] }[] = [
  { test: /architect/i, covers: ["Architecture", "Interiors"] },
  { test: /interior/i, covers: ["Interiors"] },
  { test: /structur/i, covers: ["Structural"] },
  { test: /mep|mechanical|electric|plumb/i, covers: ["MEP"] },
  { test: /landscap/i, covers: ["Landscape"] },
];
export function coveredBy(contractName: string): string[] {
  return DISCIPLINE_MATCH.find((m) => m.test.test(contractName))?.covers ?? [];
}

/* Every drawing phase is produced before site starts, so a pre-construction
   stage carries the whole register and the construction stage carries none. */
const PHASES = ["Conceptual", "Design Development", "IFT"] as const;
function phasesInStage(label: string): string[] {
  if (/course of construction|during construction|construction admin|monitoring/i.test(label)) return [];
  return [...PHASES];
}

function group(dv: Deliverable[], key: "phase" | "discipline") {
  const m = new Map<string, Deliverable[]>();
  for (const d of dv) m.set(d[key], [...(m.get(d[key]) ?? []), d]);
  return [...m.entries()];
}

/** Drawing sheets actually issued, and the renders presented, for a discipline. */
function sheetsFor(p: Project, covers: string[]) {
  return (p.reviews ?? []).flatMap((r) => r.sheets.filter((s) => covers.some((c) => (s.discipline ?? "").toLowerCase().includes(c.toLowerCase()))).map((s) => ({ ...s, review: r.title })));
}

export default function AgreedFeeDoc({ p, mode = "studio" }: { p: Project; mode?: "studio" | "client" }) {
  const b = p.billing!;
  const ccy = b.currency;
  const [disc, setDisc] = useState<string | null>(null);
  const [stage, setStage] = useState<string | null>(null);
  const deliverables = p.deliverables ?? [];
  const disciplines = b.disciplines ?? [];
  const stages = b.stages ?? [];
  const renders = (p.concepts ?? []).filter((c) => c.url);
  const allSheets = (p.reviews ?? []).flatMap((r) => r.sheets);

  const disco = feeDiscount(b);
  const basis = b.feeBasis === "percentage-of-construction" && b.constructionBudget
    ? `${((b.feePct ?? 0) * 100).toFixed(b.feePct && b.feePct * 100 % 1 ? 1 : 0)}% of ${money(b.constructionBudget, ccy)} construction budget${b.standardPct ? ` · standard rate ${(b.standardPct * 100).toFixed(0)}%` : ""}`
    : "Lump sum, as agreed";

  return (
    <div className="reveal">
      {/* header — the agreed number, not an estimate */}
      <div className="card pad" style={{ marginBottom: 18 }}>
        <div className="row between wrap" style={{ gap: 16 }}>
          <div>
            <div className="kicker">Agreed fee · {p.code}</div>
            <h3 className="serif" style={{ fontSize: 26, marginTop: 6 }}>Design Services — {p.name}</h3>
            <div className="muted" style={{ fontSize: 13.5, marginTop: 4 }}>
              {[p.client.company, p.type, b.servicePeriodMonths ? `${b.servicePeriodMonths}-month service period` : ""].filter(Boolean).join(" · ")}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="kicker">{disco ? "Agreed fee" : "Total fee"}</div>
            <div className="serif" style={{ fontSize: 32, color: "var(--accent-ink)" }}>{money(b.contractSum, ccy)}</div>
            <div className="dim" style={{ fontSize: 12.5 }}>{basis}</div>
          </div>
        </div>
        {/* the full fee and what was taken off it — the client sees both */}
        {disco && (
          <div className="panel" style={{ marginTop: 14, padding: "12px 16px", background: "color-mix(in srgb, var(--accent) 6%, var(--card))", borderColor: "color-mix(in srgb, var(--accent) 30%, var(--line))" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 14 }}>
              <div>
                <div className="kicker" style={{ fontSize: 9 }}>Full fee</div>
                <div className="serif" style={{ fontSize: 20, marginTop: 3, textDecoration: "line-through", color: "var(--ink-3)" }}>{money(disco.full, ccy)}</div>
                {disco.standardPct ? <div className="dim" style={{ fontSize: 11.5 }}>standard {(disco.standardPct * 100).toFixed(disco.standardPct * 100 % 1 ? 1 : 0)}% of construction</div> : null}
              </div>
              <div>
                <div className="kicker" style={{ fontSize: 9 }}>Discount</div>
                <div className="serif" style={{ fontSize: 20, marginTop: 3, color: "var(--accent-ink)" }}>{disco.pct.toFixed(disco.pct % 1 ? 1 : 0)}%</div>
                <div className="dim" style={{ fontSize: 11.5 }}>−{money(disco.saving, ccy)}</div>
              </div>
              <div>
                <div className="kicker" style={{ fontSize: 9 }}>You pay</div>
                <div className="serif" style={{ fontSize: 20, marginTop: 3 }}>{money(b.contractSum, ccy)}</div>
                {disco.agreedPct ? <div className="dim" style={{ fontSize: 11.5 }}>{(disco.agreedPct * 100).toFixed(disco.agreedPct * 100 % 1 ? 1 : 0)}% of construction</div> : null}
              </div>
            </div>
          </div>
        )}
        <div className="dim row wrap" style={{ gap: 7, fontSize: 11.5, marginTop: 14 }}>
          <Icon.check width={13} style={{ color: "var(--ok)", flexShrink: 0 }} />
          Read from the signed engagement{b.sourceNote ? ` — ${b.sourceNote}` : ""}. These are the agreed figures, not an estimate.
        </div>
      </div>

      {/* fee by discipline, with the drawings each one pays for */}
      {disciplines.length > 0 && (
        <div className="card" style={{ overflow: "hidden", marginBottom: 18 }}>
          <div className="row between" style={{ padding: "14px 20px", borderBottom: "1px solid var(--line)" }}>
            <div className="kicker">Fee by discipline</div>
            <div className="kicker">{b.constructionBudget ? "% of build · " : ""}% of fee · amount</div>
          </div>
          {disciplines.map((d) => {
            const amount = d.amount ?? Math.round(b.contractSum * (d.pct ?? 0));
            const open = disc === d.name;
            const covers = coveredBy(d.name);
            const dv = deliverables.filter((x) => covers.includes(x.discipline));
            const sheets = sheetsFor(p, covers);
            const pctFee = b.contractSum > 0 ? (amount / b.contractSum) * 100 : 0;
            return (
              <div key={d.name} style={{ borderBottom: "1px solid var(--line)" }}>
                <button className="row between" onClick={() => setDisc(open ? null : d.name)}
                  style={{ width: "100%", padding: "13px 20px", background: open ? "var(--paper-2)" : "transparent", border: "none", cursor: "pointer", textAlign: "left" }}>
                  <span className="row" style={{ gap: 10, minWidth: 0 }}>
                    <Icon.caretDown width={15} style={{ transform: open ? "none" : "rotate(-90deg)", transition: "transform .2s", color: "var(--ink-3)", flexShrink: 0 }} />
                    <span style={{ fontWeight: 600 }}>{d.name}</span>
                  </span>
                  <span className="row" style={{ gap: 18, flexShrink: 0 }}>
                    {b.constructionBudget ? <span className="mono dim" style={{ fontSize: 12.5, width: 54, textAlign: "right" }}>{((d.pct ?? 0) * 100).toFixed(1)}%</span> : null}
                    <span className="badge accent" style={{ minWidth: 52, justifyContent: "center" }}>{pctFee.toFixed(0)}%</span>
                    <span className="mono" style={{ fontSize: 14, width: 104, textAlign: "right" }}>{money(amount, ccy)}</span>
                  </span>
                </button>
                {open && (
                  <div style={{ padding: "4px 20px 18px 45px" }}>
                    {/* how this discipline's fee is drawn down across the agreed stages */}
                    {stages.length > 0 && (
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 8, marginBottom: 14 }}>
                        {stages.map((s) => (
                          <div key={s.label} className="panel" style={{ padding: "9px 11px" }}>
                            <div style={{ fontSize: 11.5, fontWeight: 500 }}>{s.label}</div>
                            <div className="row between" style={{ marginTop: 3 }}>
                              <span className="dim mono" style={{ fontSize: 10.5 }}>{Math.round((s.pct ?? 0) * 100)}%</span>
                              <span className="mono" style={{ fontSize: 12 }}>{money(Math.round(amount * (s.pct ?? 0)), ccy)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {dv.length > 0 ? group(dv, "phase").map(([phase, list]) => (
                      <div key={phase} style={{ marginBottom: 12 }}>
                        <div className="kicker" style={{ fontSize: 9.5, marginBottom: 6 }}>{phase} — {list.length} sheet{list.length === 1 ? "" : "s"}</div>
                        <div className="row wrap" style={{ gap: 6 }}>
                          {list.map((x) => (
                            <span key={x.id} className="badge" style={{ fontSize: 10.5 }} title={(x.contents ?? []).join(" · ")}>
                              <span className="mono">{x.code}</span> {x.title}
                            </span>
                          ))}
                        </div>
                      </div>
                    )) : <div className="dim" style={{ fontSize: 12.5 }}>No drawing register entries filed under this discipline yet.</div>}
                    {sheets.length > 0 && <SheetStrip sheets={sheets} label="Issued drawings" />}
                  </div>
                )}
              </div>
            );
          })}
          <div className="row between" style={{ padding: "16px 20px", background: "var(--paper-2)", fontWeight: 600 }}>
            <span>Total professional fee</span><span className="serif" style={{ fontSize: 20 }}>{money(b.contractSum, ccy)}</span>
          </div>
        </div>
      )}

      {/* fee by stage, with everything produced in it */}
      {stages.length > 0 && (
        <div className="card pad" style={{ marginBottom: 18 }}>
          <div className="row between" style={{ marginBottom: 14 }}>
            <div className="kicker">Fee distribution by stage</div>
            <span className="mono dim" style={{ fontSize: 12 }}>{money(b.contractSum, ccy)} total</span>
          </div>
          <div style={{ display: "flex", height: 12, borderRadius: 8, overflow: "hidden", background: "var(--line)" }}>
            {stages.map((s, i) => (
              <div key={s.label} title={`${s.label} · ${Math.round((s.pct ?? 0) * 100)}%`}
                style={{ flex: Math.max(0.001, s.pct ?? 0), background: i % 2 ? "var(--sage)" : "var(--accent)", display: "grid", placeItems: "center" }}>
                {(s.pct ?? 0) >= 0.1 && <span style={{ fontSize: 9, color: "#fff", fontWeight: 600 }} className="mono">{Math.round((s.pct ?? 0) * 100)}%</span>}
              </div>
            ))}
          </div>
          <div className="col" style={{ gap: 12, marginTop: 18 }}>
            {stages.map((s, i) => {
              const amount = s.amount ?? Math.round(b.contractSum * (s.pct ?? 0));
              const open = stage === s.label;
              const phases = phasesInStage(s.label);
              const dv = deliverables.filter((x) => phases.includes(x.phase));
              return (
                <div key={s.label} className="panel" style={{ padding: 0, borderLeft: `3px solid ${i % 2 ? "var(--sage)" : "var(--accent)"}`, overflow: "hidden" }}>
                  <button className="row between wrap" onClick={() => setStage(open ? null : s.label)}
                    style={{ width: "100%", padding: 16, gap: 10, background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}>
                    <span className="row" style={{ gap: 12, minWidth: 0 }}>
                      <span className="serif" style={{ fontSize: 20, color: "var(--ink-3)", lineHeight: 1, width: 26 }}>{String(i + 1).padStart(2, "0")}</span>
                      <span>
                        <span style={{ fontWeight: 600, fontSize: 15 }}>{s.label}</span>
                        <span className="dim" style={{ fontSize: 11.5, display: "block", marginTop: 2 }}>
                          {dv.length > 0 ? `${dv.length} drawing${dv.length === 1 ? "" : "s"} produced in this stage` : "No drawings — services rendered on site"}
                        </span>
                      </span>
                    </span>
                    <span style={{ textAlign: "right", flexShrink: 0 }}>
                      <span className="serif" style={{ fontSize: 20, display: "block" }}>{money(amount, ccy)}</span>
                      <span className="badge accent" style={{ fontSize: 10 }}>{Math.round((s.pct ?? 0) * 100)}% of fee</span>
                    </span>
                  </button>
                  {open && (
                    <div style={{ padding: "0 16px 16px 54px" }}>
                      {group(dv, "phase").map(([phase, list]) => (
                        <div key={phase} style={{ marginBottom: 12 }}>
                          <div className="kicker" style={{ fontSize: 9.5, marginBottom: 6 }}>{phase} — {list.length} sheet{list.length === 1 ? "" : "s"}</div>
                          {group(list, "discipline").map(([dname, dlist]) => (
                            <div key={dname} className="row wrap" style={{ gap: 6, marginBottom: 6 }}>
                              <span className="dim" style={{ fontSize: 11, width: 92, flexShrink: 0 }}>{dname}</span>
                              {dlist.map((x) => <span key={x.id} className="badge" style={{ fontSize: 10.5 }}><span className="mono">{x.code}</span> {x.title}</span>)}
                            </div>
                          ))}
                        </div>
                      ))}
                      {i === 0 && renders.length > 0 && <RenderStrip renders={renders} />}
                      {i === 0 && allSheets.length > 0 && <SheetStrip sheets={allSheets.map((s2) => ({ ...s2, review: "" }))} label="Issued drawings" />}
                      {dv.length === 0 && (
                        <div className="dim" style={{ fontSize: 12.5 }}>
                          Contract administration, site monitoring and certification — billed against progress rather than drawings.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* deferred terms — the client should never have to hunt for this */}
      {b.deferred && (
        <div className="card pad" style={{ marginBottom: 18, background: "color-mix(in srgb, var(--warn) 7%, transparent)" }}>
          <div className="kicker">Deferred amount · {Math.round(b.deferred.pct * 100)}% of every invoice</div>
          <div className="serif" style={{ fontSize: 22, marginTop: 5 }}>{money(Math.round(b.contractSum * b.deferred.pct), ccy)} deferred at full value</div>
          <p className="muted" style={{ fontSize: 13.5, marginTop: 8, lineHeight: 1.6 }}>Payable {b.deferred.trigger}</p>
          {b.deferred.note && <p className="dim" style={{ fontSize: 12, marginTop: 8, lineHeight: 1.6 }}>{b.deferred.note}</p>}
        </div>
      )}

      {/* extras — chargeable outside the fee */}
      {(b.additionalServices ?? []).length > 0 && (
        <div className="card" style={{ overflow: "hidden", marginBottom: 18 }}>
          <div className="row between" style={{ padding: "14px 20px", borderBottom: "1px solid var(--line)" }}>
            <div className="kicker">Additional services</div><div className="kicker">charged outside the fee</div>
          </div>
          {b.additionalServices!.map((s, i) => (
            <div key={i} className="row between wrap" style={{ padding: "11px 20px", borderTop: i ? "1px solid var(--line)" : "none", gap: 10 }}>
              <span style={{ minWidth: 0 }}>
                <span style={{ fontSize: 13.5 }}>{s.label}</span>
                {(s.included || s.note) && (
                  <span className="dim" style={{ fontSize: 11.5, display: "block", marginTop: 2 }}>
                    {s.included ? `${s.included} included` : ""}{s.included && s.note ? " · " : ""}{s.note ?? ""}
                  </span>
                )}
              </span>
              <span className="mono" style={{ fontSize: 13, flexShrink: 0 }}>
                {s.rate ? `${money(s.rate, ccy)}${s.rateMax ? `–${money(s.rateMax, ccy)}` : ""}${s.unit ? ` / ${s.unit}` : ""}` : "On application"}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* what the fee does not cover */}
      {(b.exclusions ?? []).length > 0 && (
        <div className="card pad">
          <div className="kicker" style={{ marginBottom: 10 }}>Not included in the fee</div>
          <div className="col" style={{ gap: 7 }}>
            {b.exclusions!.map((x, i) => (
              <div key={i} className="row" style={{ gap: 8, fontSize: 13.5, alignItems: "flex-start" }}>
                <span className="dim" style={{ flexShrink: 0 }}>—</span><span className="muted">{x}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {mode === "client" && (
        <p className="dim" style={{ fontSize: 12, marginTop: 14, textAlign: "center" }}>
          Every figure here is taken from your signed engagement. Payments against it are in <b>Billing</b>.
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------- visuals */

function SheetStrip({ sheets, label }: { sheets: { id: string; code: string; title: string; revision: string; url?: string; hue: number }[]; label: string }) {
  return (
    <div style={{ marginTop: 12 }}>
      <div className="kicker" style={{ fontSize: 9.5, marginBottom: 6 }}>{label} ({sheets.length})</div>
      <div className="row wrap" style={{ gap: 10 }}>
        {sheets.map((s) => (
          <div key={s.id} className="panel" style={{ padding: 0, width: 128, overflow: "hidden" }}>
            <div style={{ height: 84, background: s.url ? `center/cover no-repeat url(${s.url})` : `hsl(${s.hue} 30% 88%)` }} />
            <div style={{ padding: "6px 8px" }}>
              <div className="mono" style={{ fontSize: 10 }}>{s.code} · {s.revision}</div>
              <div style={{ fontSize: 10.5, lineHeight: 1.35, marginTop: 2 }}>{s.title}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RenderStrip({ renders }: { renders: { id: string; title: string; url?: string; hue: number }[] }) {
  return (
    <div style={{ marginTop: 4, marginBottom: 12 }}>
      <div className="kicker" style={{ fontSize: 9.5, marginBottom: 6 }}>Renders & design directions ({renders.length})</div>
      <div className="row wrap" style={{ gap: 10 }}>
        {renders.map((r) => (
          <div key={r.id} className="panel" style={{ padding: 0, width: 158, overflow: "hidden" }}>
            <div style={{ height: 98, background: r.url ? `center/cover no-repeat url(${r.url})` : `hsl(${r.hue} 40% 86%)` }} />
            <div style={{ padding: "6px 8px", fontSize: 10.5 }}>{r.title}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
